import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SectionHeading } from "@/components/bespoke/section-heading";
import type { SkillSource, SkillStatus, SkillUpdateState } from "@zworks/console-contracts";
import { useNavigate } from "react-router";
import { useState } from "react";
import { EmptyState, ErrorState, ForbiddenState, LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { ApiError, approveSkill, installSkill, removeSkill, skillsQuery } from "@/lib/api";
import { PageHeader } from "@/components/bespoke";
import { CapabilityDrawer, type CapabilityDrawerItem } from "@/components/capability-drawer";
import { useCurrentProject } from "@/lib/project";

const updateBadges: Record<
  SkillUpdateState,
  { label: string; variant: "success" | "warning" | "info" }
> = {
  "up-to-date": { label: "Up to date", variant: "success" },
  "update-available": { label: "Update available", variant: "warning" },
  updating: { label: "Updating", variant: "info" },
};

const sourceLabels: Record<SkillSource, string> = {
  marketplace: "Marketplace",
  uploaded: "Uploaded",
};

const statusBadges: Record<SkillStatus, { label: string; variant: "info" | "warning" | "muted" }> =
  {
    enabled: { label: "Enabled", variant: "info" },
    trial: { label: "Trial", variant: "warning" },
    private: { label: "Private", variant: "muted" },
    // The platform withdrew this pack's digest. It stays in the list rather than vanishing, so
    // a person looking for what they installed is not left wondering where it went.
    revoked: { label: "Revoked", variant: "muted" },
  };

function Summary({
  installed,
  upToDate,
  updates,
  workflows,
  revocationChecked,
}: {
  installed: number;
  revocationChecked: boolean;
  upToDate: number;
  updates: number;
  workflows: number;
}) {
  const cards = [
    { label: "Installed", value: String(installed) },
    { label: "Up to date", value: revocationChecked ? String(upToDate) : "Unknown" },
    { label: "Updates available", value: String(updates) },
    { label: "Used by workflows", value: String(workflows) },
  ];
  return (
    <dl className="grid gap-3 sm:grid-cols-4">
      {cards.map((card) => (
        <div key={card.label} className="rounded-[var(--radius-card)] border bg-card px-4 py-3">
          <dt className="text-xs font-medium text-muted-foreground">{card.label}</dt>
          <dd className="mt-1 text-2xl font-semibold tabular-nums">{card.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function EvalScore({ score }: { score: number | null | undefined }) {
  if (score === null || score === undefined) {
    return <Badge variant="unknown">Not run</Badge>;
  }
  return (
    <Badge variant={score >= 80 ? "success" : score >= 60 ? "warning" : "danger"}>{score}%</Badge>
  );
}

function InstallSkillMenu() {
  const navigate = useNavigate();
  return (
    <div className="relative">
      <DropdownMenu>
        <DropdownMenuTrigger className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 md:h-9">
          Install skill
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => void navigate("/marketplace")}>
            From Marketplace
          </DropdownMenuItem>
          <DropdownMenuItem disabled title="Skill upload lands with the skill upload flow">
            Upload your own
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/** The Studio skills tab: installed skills with source, version, digest, eval score, use, and state. */
export function SkillsPage({ headingLevel = "h1" }: { headingLevel?: "h1" | "h2" }) {
  const { project, loaded } = useCurrentProject();
  const queryClient = useQueryClient();
  const skills = useQuery({
    ...skillsQuery(project?.id ?? ""),
    enabled: loaded && project !== null,
  });
  const [name, setName] = useState("");
  const [version, setVersion] = useState("");
  const [digest, setDigest] = useState("");
  const [feedback, setFeedback] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["projects", project?.id, "skills"] });
  };
  const normalisedDigest = digest.trim();
  const digestValid = /^sha256:[0-9a-f]{64}$/.test(normalisedDigest);
  const approve = useMutation({
    mutationFn: (installId: string) => approveSkill(project!.id, installId),
    onSuccess: () => {
      setFeedback("Skill approved. A workflow can bind it now.");
      refresh();
    },
    onError: (caught) => {
      setFeedback(
        caught instanceof ApiError
          ? (caught.problem?.detail ?? caught.message)
          : "The skill could not be approved.",
      );
    },
  });
  const installMutation = useMutation({
    mutationFn: () =>
      installSkill(project!.id, {
        skill: name.trim(),
        version: version.trim(),
        digest: normalisedDigest,
      }),
    onSuccess: async () => {
      setName("");
      setVersion("");
      setDigest("");
      setFeedback("Skill installed.");
      refresh();
    },
    onError: () =>
      setFeedback("The skill could not be installed. Check the details and try again."),
  });

  if (!loaded || (project !== null && skills.isPending)) {
    return (
      <>
        <PageHeader title="Skills" headingLevel={headingLevel} />
        <LoadingState label="Loading skills" rows={5} />
      </>
    );
  }

  if (project === null) {
    return (
      <>
        <PageHeader title="Skills" headingLevel={headingLevel} />
        <EmptyState
          title="No projects yet"
          description="Create a project to install the skills its runs may use."
        />
      </>
    );
  }

  if (skills.isError) {
    const error = skills.error;
    return (
      <>
        <PageHeader title="Skills" headingLevel={headingLevel} />
        {error instanceof ApiError && error.status === 404 ? (
          <ForbiddenState
            title="You do not have access to this project"
            description="Ask an owner to invite you. Nothing from the project is shown."
          />
        ) : (
          <ErrorState
            title="Skills could not be loaded"
            description={
              error instanceof ApiError
                ? (error.problem?.detail ?? error.message)
                : "The request could not be completed."
            }
            requestId={error instanceof ApiError ? (error.requestId ?? "unknown") : "unknown"}
            onRetry={() => void skills.refetch()}
          />
        )}
      </>
    );
  }

  const skillRows = skills.data?.list ?? [];
  // When the revocation reconcile failed, no row's "up to date" means what it says: the
  // platform could not be asked whether a skill has been withdrawn. The tiles and the badges
  // both stop claiming it, rather than the page quietly showing the good answer.
  const revocationChecked = skills.data?.revocationChecked ?? true;
  const upToDate = skillRows.filter((row) => row.updateState === "up-to-date").length;
  const updates = skillRows.filter((row) => row.updateState === "update-available").length;
  const workflows = new Set(skillRows.flatMap((row) => row.usedIn ?? [])).size;
  const selected = skillRows.find((row) => row.id === selectedId) ?? null;
  const selectedItem: CapabilityDrawerItem | null = selected
    ? {
        name: selected.skill,
        detail: selected.digest,
        // The permission list arrives only from the reviewed catalogue row through the
        // install record. Anything without one shows the explicit unknown state: the
        // version, source, status, and digest fields never stand in for permissions.
        permissions: selected.permissions ?? [],
        permissionsUnknown: !selected.permissions,
        version: selected.version,
        source: selected.source ? sourceLabels[selected.source] : undefined,
        state: updateBadges[selected.updateState],
        workflows: selected.usedIn ?? [],
      }
    : null;
  const canInstall =
    name.trim().length > 0 && version.trim().length > 0 && digest.trim().length > 0;
  const digestTouched = digest.length > 0;
  const digestHint =
    !digestTouched || digestValid
      ? "sha256: followed by 64 hexadecimal characters. The digest pins the exact content installed."
      : "Enter a pinned digest: sha256: followed by 64 hexadecimal characters.";

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Skills" headingLevel={headingLevel} actions={<InstallSkillMenu />} />

      <Summary
        installed={skillRows.length}
        upToDate={upToDate}
        updates={updates}
        workflows={workflows}
        revocationChecked={revocationChecked}
      />

      {!revocationChecked ? (
        <p role="alert" className="text-sm text-danger">
          Revocation status could not be checked. The platform did not answer, so a skill shown as
          up to date may have been withdrawn since. Try again in a moment.
        </p>
      ) : null}

      {feedback && (
        <p role="status" className="text-sm text-muted-foreground">
          {feedback}
        </p>
      )}

      {skillRows.length === 0 ? (
        <EmptyState
          title="No skills installed yet"
          description="Install a reviewed skill from the Marketplace."
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <caption className="sr-only">
                  Installed skills with source, version, eval score, use, and state
                </caption>
                <thead>
                  <tr>
                    {["Skill", "Source", "Version", "Eval", "Used in", "State"].map((label) => (
                      <th
                        key={label}
                        scope="col"
                        className="h-10 border-b px-3 text-left text-xs font-medium text-muted-foreground first:pl-5 last:pr-5"
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {skillRows.map((row) => {
                    const update = updateBadges[row.updateState];
                    const status = row.status ? statusBadges[row.status] : null;
                    const usedIn = row.usedIn ?? [];
                    return (
                      <tr key={row.id} className="hover:bg-muted/50">
                        <td className="border-b px-3 py-3 first:pl-5">
                          <button
                            type="button"
                            className="rounded-sm text-left font-mono text-[0.8125rem] underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                            onClick={() => setSelectedId(row.id)}
                            aria-label={`Open ${row.skill} details`}
                          >
                            {row.skill}
                          </button>
                          {row.digest && (
                            <span className="mt-1 block font-mono text-xs text-muted-foreground">
                              {row.digest.length > 18 ? `${row.digest.slice(0, 18)}…` : row.digest}
                            </span>
                          )}
                        </td>
                        <td className="border-b px-3 py-3 text-muted-foreground">
                          {row.source ? sourceLabels[row.source] : "Marketplace"}
                        </td>
                        <td className="border-b px-3 py-3 font-mono text-[0.8125rem]">
                          v{row.version}
                          {status && (
                            <span className="ml-2">
                              <Badge variant={status.variant}>{status.label}</Badge>
                            </span>
                          )}
                        </td>
                        <td className="border-b px-3 py-3">
                          <EvalScore score={row.evalScore} />
                        </td>
                        <td className="border-b px-3 py-3">
                          {usedIn.length === 0 ? (
                            <span className="text-muted-foreground">No workflows yet</span>
                          ) : (
                            <span>
                              {usedIn.length} workflow{usedIn.length === 1 ? "" : "s"}
                            </span>
                          )}
                        </td>
                        <td className="border-b px-3 py-3">
                          <Badge variant={update.variant}>{update.label}</Badge>
                        </td>
                        <td className="border-b px-3 py-3 last:pr-5">
                          {/* Only a private install is waiting on somebody. An enabled one has
                              nothing to approve, and a control for it would invite a decision
                              that changes nothing. */}
                          {row.status === "private" ? (
                            confirmingId === row.id ? (
                              // The approver sees what they are handing over before it happens:
                              // an approval is a decision about authority, and a bare button
                              // gives them nothing to decide on.
                              <span className="flex flex-col items-start gap-2">
                                <span className="text-xs text-muted-foreground">
                                  {row.permissions && row.permissions.length > 0
                                    ? `Approving grants: ${row.permissions.join(", ")}.`
                                    : "This install declared no permissions, so approving grants nothing beyond the skill itself."}
                                </span>
                                <span className="flex flex-wrap items-center gap-2">
                                  <Button
                                    type="button"
                                    size="sm"
                                    disabled={approve.isPending}
                                    onClick={() => {
                                      approve.mutate(row.id);
                                      setConfirmingId(null);
                                    }}
                                  >
                                    Confirm approval
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setConfirmingId(null)}
                                  >
                                    Cancel
                                  </Button>
                                </span>
                              </span>
                            ) : (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={approve.isPending}
                                onClick={() => setConfirmingId(row.id)}
                              >
                                Review and approve
                              </Button>
                            )
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <section aria-labelledby="install-skill-heading">
        <Card>
          <CardContent>
            <SectionHeading id="install-skill-heading">Install a skill</SectionHeading>
            <form
              className="mt-3 grid gap-3 sm:grid-cols-3"
              onSubmit={(event) => {
                event.preventDefault();
                installMutation.mutate();
              }}
            >
              <label className="grid gap-1 text-sm">
                Skill name
                <Input
                  aria-label="Skill name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="code-reviewer"
                />
              </label>
              <label className="grid gap-1 text-sm">
                Version
                <Input
                  aria-label="Version"
                  value={version}
                  onChange={(event) => setVersion(event.target.value)}
                  placeholder="0.2.0"
                />
              </label>
              <label className="grid gap-1 text-sm">
                Digest (required)
                <Input
                  aria-label="Digest"
                  aria-invalid={digestTouched && !digestValid}
                  aria-describedby="install-digest-hint"
                  value={digest}
                  onChange={(event) => setDigest(event.target.value)}
                  placeholder="sha256:…"
                />
              </label>
              <div>
                <Button
                  type="submit"
                  disabled={!canInstall || !digestValid || installMutation.isPending}
                >
                  {installMutation.isPending ? "Installing…" : "Install skill"}
                </Button>
              </div>
            </form>
            <p id="install-digest-hint" className="mt-3 text-xs text-muted-foreground">
              {digestHint} Source and status are decided by the server: reviewed Marketplace
              installs arrive enabled, everything else stays private until an approver confirms it.
            </p>
          </CardContent>
        </Card>
      </section>

      <CapabilityDrawer
        item={selectedItem}
        kind="skill"
        onClose={() => setSelectedId(null)}
        onRemove={async () => {
          if (!project || !selected) return;
          await removeSkill(project.id, selected.id);
          setFeedback("Skill removed.");
          refresh();
        }}
      />
    </div>
  );
}
